/** Categoria do sinal; decide a amostragem (U30). */
export type SignalKind = 'navigation' | 'http' | 'error' | 'web-vital' | 'agent';

export type AttributeValue = string | number | boolean;
export type Attributes = Record<string, AttributeValue | undefined>;

export interface SpanEvent {
  name: string;
  timeUnixNano: string;
  attributes?: Attributes;
}

export interface SpanRecord {
  kind: SignalKind;
  name: string;
  traceId: string;
  spanId: string;
  startTimeUnixNano: string;
  endTimeUnixNano: string;
  client?: boolean;
  attributes: Attributes;
  status?: { code: 'ok' | 'error'; message?: string };
  events?: SpanEvent[];
}

type OtlpAnyValue = { stringValue: string } | { intValue: string } | { doubleValue: number } | { boolValue: boolean };
interface OtlpKeyValue {
  key: string;
  value: OtlpAnyValue;
}

// Enums do OTLP (opentelemetry-proto): SpanKind INTERNAL=1, CLIENT=3; StatusCode OK=1, ERROR=2.
const SPAN_KIND_INTERNAL = 1;
const SPAN_KIND_CLIENT = 3;
const STATUS_CODE = { ok: 1, error: 2 } as const;

function toAnyValue(value: AttributeValue): OtlpAnyValue {
  if (typeof value === 'boolean') return { boolValue: value };
  if (typeof value === 'number') {
    return Number.isInteger(value) ? { intValue: String(value) } : { doubleValue: value };
  }
  return { stringValue: value };
}

/** Atributos no formato OTLP/JSON; `undefined` some. */
export function toOtlpAttributes(attributes: Attributes): OtlpKeyValue[] {
  const out: OtlpKeyValue[] = [];
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined) continue;
    out.push({ key, value: toAnyValue(value) });
  }
  return out;
}

/** Corpo de `POST /v1/traces` em OTLP/HTTP JSON, um `resourceSpans` por lote. */
export function toOtlpPayload(resource: Attributes, spans: readonly SpanRecord[], scopeVersion = '1') {
  return {
    resourceSpans: [
      {
        resource: { attributes: toOtlpAttributes(resource) },
        scopeSpans: [
          {
            scope: { name: 'uyzy-web', version: scopeVersion },
            spans: spans.map((span) => ({
              traceId: span.traceId,
              spanId: span.spanId,
              name: span.name,
              kind: span.client ? SPAN_KIND_CLIENT : SPAN_KIND_INTERNAL,
              startTimeUnixNano: span.startTimeUnixNano,
              endTimeUnixNano: span.endTimeUnixNano,
              attributes: toOtlpAttributes({ 'uyzy.signal': span.kind, ...span.attributes }),
              ...(span.events?.length
                ? {
                    events: span.events.map((event) => ({
                      name: event.name,
                      timeUnixNano: event.timeUnixNano,
                      attributes: toOtlpAttributes(event.attributes ?? {}),
                    })),
                  }
                : {}),
              ...(span.status
                ? { status: { code: STATUS_CODE[span.status.code], ...(span.status.message ? { message: span.status.message } : {}) } }
                : {}),
            })),
          },
        ],
      },
    ],
  };
}

/** Id aleatório em hex (16 bytes para trace, 8 para span), como o OTLP/JSON pede. */
export function randomHex(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(buffer);
  return Array.from(buffer, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Epoch em nanossegundos como string decimal (int64 não cabe em `number`). */
export function msToUnixNano(epochMs: number): string {
  const whole = Math.floor(epochMs);
  const fraction = Math.round((epochMs - whole) * 1e6);
  return (BigInt(whole) * 1_000_000n + BigInt(fraction)).toString();
}

export function nowMs(): number {
  return globalThis.performance.timeOrigin + globalThis.performance.now();
}

/** Coletor → endpoint de traces: aceita a base ou o caminho completo. */
export function tracesEndpoint(collectorUrl: string): string {
  const trimmed = collectorUrl.replace(/\/+$/, '');
  return trimmed.endsWith('/v1/traces') ? trimmed : `${trimmed}/v1/traces`;
}
